import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { IsolationVoltage } from '../fields/IsolationVoltage';
import { AllPortControlMethods } from '../fields/AbstractPort.ControlMethods';
import { PortPurpose } from '../fields';

import { AbstractPort, Port } from '../fields/AbstractPort';
import { Current } from '../fields/Current';
import { ParallelableCapacity } from '../fields/ParallelableCapacity';
import { Resistance } from '../fields/Resistance';
import { Inductance } from '../fields/Inductance';
import { Capacitance } from '../fields/Capacitance';

import { environmentalWithCoolingMethod } from '../component-sections/environmental';

import { ComponentDefinition } from '../../types/ComponentDefinition';

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    DC: Port.DC.extend({
        controlMethods: Port.controlMethods.validator,
        faultSteadyStateCurrent: Current.validators.nomOptionalMinMax.optional(),
    }).default({}),
    capacitor: z
        .object({
            capacitance: Capacitance.validator,
            resistance: Resistance.validator,
        })
        .default({}),
    reverseDiode: z.boolean().default(false),
    isolated: Port.isolated.validator,
    parallelableCapacity: ParallelableCapacity.validator,
    purpose: PortPurpose.validator,
    resistance: Resistance.validator,
    inductance: Inductance.validators.value,
    faultFeedingPorts: z.array(z.number()).default([]),
}).default({
    powerFlowDirection: 'bidirectional',
});

const efficiencyPoint = z.object({
    power: z.number().nullable(),
    efficiency: z.number(),
});

const efficiencyCurve = z.object({
    voltages: z.array(z.number()).optional(),
    points: z
        .array(efficiencyPoint, { required_error: 'An efficiency curve needs at least one point' })
        .min(1, 'An efficiency curve needs at least one point'),
});

const efficiency = z.object({
    ports: z.array(z.number().int().min(0)).optional(),
    curves: z.array(efficiencyCurve).min(1),
});

const validateEfficiency = (
    electrical: { ports: z.infer<typeof port>[]; efficiency?: z.infer<typeof efficiency> },
    context: z.RefinementCtx,
) => {
    if (!electrical.efficiency) {
        return;
    }

    const { ports = [], curves } = electrical.efficiency;
    const voltageSets = curves.map(({ voltages = [] }) => voltages);
    const points = curves.flatMap((curve) => curve.points);
    const constant = points.length === 1 && points[0].power === null ? points[0].efficiency : undefined;
    const addIssue = (message: string) =>
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['efficiency'], message });

    if (new Set(ports).size < ports.length || ports.some((index) => !electrical.ports[index]?.DC.enabled)) {
        addIssue('Efficiency voltages must be on different DC ports of the converter');
    }

    if (voltageSets.some((voltages) => voltages.length !== ports.length || voltages.some((voltage) => voltage <= 0))) {
        addIssue('Every efficiency curve needs a voltage above 0 V for each of its ports');
    }

    if (new Set(voltageSets.map(String)).size < voltageSets.length) {
        addIssue('Two efficiency curves have the same voltages');
    }

    if (constant === undefined && points.some((point) => point.power === null)) {
        addIssue('Only a constant efficiency can leave the power empty');
    }

    if (constant !== undefined && (constant < 1 || constant > 100)) {
        addIssue('A constant efficiency must be between 1 and 100 %');
    }
};

const ConverterBase = z.object({
    type: z.literal('converter').default('converter'),
    electrical: z
        .object({
            ports: z.array(port).min(2).default([undefined, undefined]),
            standards: z.array(z.string()).default([]),
            isolationVoltage: IsolationVoltage.validator,
            efficiency: efficiency.optional(),
        })
        .superRefine(validateEfficiency)
        .default({}),
    environmental: environmentalWithCoolingMethod,
});

export const ConverterValidator = ComponentValidator.merge(ConverterBase);
export const ConverterPayloadValidator = ComponentPayloadValidator.merge(ConverterBase);

export const ConverterDefinition: ComponentDefinition = {
    type: 'converter',
    name: 'Converter',
    plural: 'Converters',
    indicator: 'U',
    actsAsConverter: true,
    hasDynamicNumberOfPorts: true,
    interoperabilityDataModel: true,
    ports: {
        min: 2,
        max: Infinity,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        allowedControlMethods: AllPortControlMethods,
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: ConverterValidator,
        payload: ConverterPayloadValidator,
    },
};

export type Converter = z.infer<typeof ConverterValidator>;
export type ConverterPayload = z.infer<typeof ConverterPayloadValidator>;
